import {
  Bell,
  BellSlash,
  CheckCircle,
  ClockCountdown,
  DeviceTablet,
  Factory,
  FileText,
  GearSix,
  Lifebuoy,
  Ruler,
  Trash,
  Truck,
  UserMinus,
  UserPlus,
  Warning,
  type Icon,
} from '@phosphor-icons/react';
import type { Notification, NotificationType } from '@sm/api';
import { useNavigate } from '@tanstack/react-router';
import { Popover as P } from 'radix-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cx, toneClass, type Tone } from '@/components/ui/cx';
import { fmtAgo } from '@/lib/format';
import { notificationTarget } from '@/lib/links';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/lib/queries';

const TYPE_UI: Record<NotificationType, { icon: Icon; tone: Tone }> = {
  project_assigned: { icon: Ruler, tone: 'info' },
  measurement_submitted: { icon: Ruler, tone: 'info' },
  measurement_approved: { icon: CheckCircle, tone: 'ok' },
  measurement_returned: { icon: Warning, tone: 'danger' },
  field_warning: { icon: Warning, tone: 'caution' },
  production_order_created: { icon: Factory, tone: 'info' },
  station_bottleneck: { icon: Factory, tone: 'danger' },
  quality_passed: { icon: CheckCircle, tone: 'ok' },
  deadline_missed: { icon: ClockCountdown, tone: 'danger' },
  document_generated: { icon: FileText, tone: 'info' },
  invitation_pending: { icon: UserPlus, tone: 'info' },
  user_joined: { icon: UserPlus, tone: 'ok' },
  project_created: { icon: Ruler, tone: 'info' },
  project_phase_changed: { icon: Truck, tone: 'ok' },
  account_deletion_requested: { icon: UserMinus, tone: 'caution' },
  company_deletion_scheduled: { icon: Trash, tone: 'danger' },
  support_report_submitted: { icon: Lifebuoy, tone: 'info' },
  device_critical: { icon: DeviceTablet, tone: 'danger' },
  support_access: { icon: Lifebuoy, tone: 'caution' },
  tenant_approved: { icon: CheckCircle, tone: 'ok' },
  plan_changed: { icon: CheckCircle, tone: 'info' },
};

export function NotificationsPopover({ canSettings }: { canSettings: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'all' | 'unread'>('all');
  const { data } = useNotifications();
  const markOne = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const [localRead, setLocalRead] = useState<Set<string>>(new Set());

  const items = (data?.items ?? []).map((n) =>
    localRead.has(n.id) && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n,
  );
  const unread = items.filter((n) => !n.readAt);
  const list = tab === 'unread' ? unread : items;

  const openItem = (n: Notification) => {
    if (!n.readAt) {
      setLocalRead((s) => new Set(s).add(n.id));
      markOne.mutate(n.id);
    }
    setOpen(false);
    const target = notificationTarget(n.link);
    if (target) void navigate(target);
  };

  return (
    <P.Root open={open} onOpenChange={setOpen}>
      <P.Trigger
        aria-label={t('panel:notifications.title')}
        className={cx(
          'relative grid h-10 w-10 place-items-center rounded-md border text-text transition-colors hover:border-accent hover:text-accent',
          open ? 'border-accent bg-chip' : 'border-line2 bg-transparent',
        )}
        data-testid="notifications-trigger"
      >
        <Bell size={18} />
        {unread.length > 0 ? (
          <span
            data-testid="notifications-dot"
            className="absolute top-2 right-[9px] h-2 w-2 rounded-full border-2 border-surface bg-danger"
          />
        ) : null}
      </P.Trigger>
      <P.Portal>
        <P.Content
          align="end"
          sideOffset={8}
          aria-label={t('panel:notifications.title')}
          data-testid="notifications-panel"
          className="z-[41] flex w-[400px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-card outline-none"
        >
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3.5">
            <span className="flex items-center gap-2 font-heading text-[15px] font-semibold">
              {t('panel:notifications.title')}
              {unread.length > 0 ? (
                <span className="rounded-full bg-danger-bg px-[7px] py-px font-body text-[11px] font-semibold text-danger">
                  {unread.length}
                </span>
              ) : null}
            </span>
            <button
              type="button"
              disabled={unread.length === 0}
              data-testid="notifications-mark-all"
              onClick={() => {
                setLocalRead(new Set(items.map((n) => n.id)));
                markAll.mutate();
              }}
              className="border-0 bg-transparent p-0 text-[13px] font-semibold text-accent disabled:opacity-50"
            >
              {t('panel:notifications.markAllRead')}
            </button>
          </div>
          <div role="tablist" className="flex gap-1 border-b border-line px-3 py-2">
            {(
              [
                ['all', t('panel:notifications.tabs.all')],
                ['unread', t('panel:notifications.tabs.unreadWithCount', { count: unread.length })],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                role="tab"
                aria-selected={tab === k}
                type="button"
                onClick={() => setTab(k)}
                className={cx(
                  'min-h-[30px] rounded-sm border-0 px-3 text-[13px] font-semibold',
                  tab === k ? 'bg-chip text-accent' : 'bg-transparent text-muted hover:text-text',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex max-h-[420px] flex-col overflow-auto">
            {list.map((n) => {
              const ui = TYPE_UI[n.type];
              const Icon = ui.icon;
              const isRead = !!n.readAt;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => openItem(n)}
                  data-testid="notification-item"
                  data-type={n.type}
                  data-read={isRead}
                  className={cx(
                    'grid grid-cols-[34px_minmax(0,1fr)_8px] items-start gap-3 border-0 border-b border-line px-4 py-[13px] text-left text-text hover:bg-bg2',
                    isRead ? 'bg-transparent' : 'bg-bg2',
                  )}
                >
                  <span
                    className={cx(
                      'grid h-[34px] w-[34px] place-items-center rounded-md',
                      toneClass[ui.tone],
                    )}
                  >
                    <Icon size={17} />
                  </span>
                  <span className="flex min-w-0 flex-col gap-[3px]">
                    <span className="text-sm leading-[1.4] font-semibold">{n.title}</span>
                    <span className="text-[13px] leading-[1.45] text-text2">{n.body}</span>
                    <span className="text-xs text-muted">{fmtAgo(n.createdAt, t)}</span>
                  </span>
                  <span
                    aria-label={isRead ? undefined : t('panel:notifications.unreadDot')}
                    className={cx(
                      'mt-1.5 h-2 w-2 rounded-full',
                      isRead ? 'bg-transparent' : 'bg-accent',
                    )}
                  />
                </button>
              );
            })}
            {list.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-4 py-9 text-sm text-muted">
                <BellSlash size={26} />
                {tab === 'unread'
                  ? t('panel:notifications.empty')
                  : t('panel:notifications.emptyAll')}
              </div>
            ) : null}
          </div>
          {canSettings ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void navigate({ to: '/podesavanja', search: { tab: 'profil' } });
              }}
              className="flex min-h-11 items-center justify-center gap-1.5 border-0 bg-bg2 text-[13px] font-semibold text-text hover:text-accent"
            >
              <GearSix size={15} />
              {t('panel:notifications.settings')}
            </button>
          ) : null}
        </P.Content>
      </P.Portal>
    </P.Root>
  );
}
